import React from 'react';
import { takeLatest, put, call, delay } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import {
  FIRST_AUTH_LOGIN,
  AUTH_LOGOUT,
  AUTH_RESET_WITH_TOKEN,
  LOADER_CLOSE,
  LOADER_OPEN,
  AUTH_RESTORE_PASSWORD,
  OTP_AUTH_LOGIN,
  AUTH_LOGIN_SSO,
  AUTH_LOGIN,
  ACTION_TRACKING,
} from '../actions';
import auth from '../../api/auth';
import routes from '../../../routes';
import { HOME_CONFIGURATION_GET } from '../actions/home';
import { ToastError, ToastSuccess } from '../../../ui/components/Toast';
import { SAVE_TEMP_TOKEN } from '../actions/user';
import storage from '../../api/storage';

function* authLogin({ params, navigate }) {
  try {
    yield put({ type: LOADER_OPEN });
    const { username, password } = params;
    const body = { username, password };
    const { data: { data = {} } = {} } = yield call(auth.login, body);

    if (data?.force_password_reset) {
      yield navigate(routes.resetAuth.path);
    } else if (data?.otp_enabled) {
      yield put({ type: FIRST_AUTH_LOGIN._SUCCESS, data });
      yield navigate(routes.otpAuth.path);
    } else if (!data?.region_province_set) {
      const { token } = data;
      yield put({ type: SAVE_TEMP_TOKEN, token });
      yield navigate(routes.authSet.path);
    } else {
      const { token } = data;
      yield navigate(routes.home.path);
      // need to effectively change route
      yield delay(0);
      yield put({ type: AUTH_LOGIN._SUCCESS, token });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('sagas > auth > authLogin', err);
    toast.error(
      <ToastError
        title="Attenzione"
        body="Username e/o password non corretti"
      />
    );
    yield put({ type: FIRST_AUTH_LOGIN._ERROR, response: err });
  } finally {
    yield put({ type: LOADER_CLOSE });
  }
}

function* authLoginWatch() {
  yield takeLatest(FIRST_AUTH_LOGIN._REQUEST, authLogin);
}

function* otpAuthLogin({ otp, navigate }) {
  try {
    yield put({ type: LOADER_OPEN });
    const otp_private = storage.read('otp_private')?.value;
    const body = { otp, otp_private };
    const { data: { data } = {} } = yield call(auth.loginOTP, body);
    if (data?.force_password_reset) {
      yield navigate(routes.resetAuth.path);
    } else if (!data?.region_province_set) {
      const { token } = data;
      yield put({ type: SAVE_TEMP_TOKEN, token });
      yield navigate(routes.authSet.path);
    } else {
      const { token } = data;
      yield navigate(routes.home.path);
      // need to effectively change route
      yield delay(0);
      yield put({ type: OTP_AUTH_LOGIN._SUCCESS, token });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('sagas > auth > otpAuthLogin', err);
    toast.error(
      <ToastError title="Attenzione" body="Il codice OTP non è corretto" />
    );
    yield put({ type: OTP_AUTH_LOGIN._ERROR, response: err });
  } finally {
    yield put({ type: LOADER_CLOSE });
  }
}
function* otpAuthLoginWatch() {
  yield takeLatest(OTP_AUTH_LOGIN._REQUEST, otpAuthLogin);
}

function* authLoginSSO({ tokenSSO, navigate }) {
  try {
    yield put({ type: LOADER_OPEN });
    const payload = {
      tokenSSO,
    };
    const { data: { data: { token, roles, region_province_set } = {} } = {} } =
      yield call(auth.loginSSO, payload);
    if (!region_province_set) {
      yield put({ type: SAVE_TEMP_TOKEN, token });
      yield navigate(routes.authSet.path);
    } else {
      yield navigate(routes.home.path);
      // need to effectively change route
      yield delay(0);
      yield put({ type: AUTH_LOGIN_SSO._SUCCESS, token, roles });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('sagas > auth > authLoginSSO', err);
    yield navigate(routes.auth.path);
    yield put({ type: AUTH_LOGIN_SSO._ERROR, response: err });
    toast.error(
      <ToastError
        title="Accesso negato"
        body="Qualcosa è andato storto durante l'autenticazione"
      />
    );
  } finally {
    yield put({ type: LOADER_CLOSE });
  }
}

function* authLoginSSOWatch() {
  yield takeLatest(AUTH_LOGIN_SSO._REQUEST, authLoginSSO);
}

function* authRestorePassword({ params: { email } = {} }) {
  try {
    yield put({ type: LOADER_OPEN });
    yield call(auth.restorePassword, { mail: email });
    yield put({ type: AUTH_RESTORE_PASSWORD._SUCCESS });
    toast.success(
      <ToastSuccess
        title="Email inviata"
        body="Controlla la casella di posta elettronica"
      />
    );
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('sagas > auth > authRestorePassword', err);
    yield put({ type: AUTH_RESTORE_PASSWORD._ERROR, response: err });
    toast.error(<ToastError body={err} />);
  } finally {
    yield put({ type: LOADER_CLOSE });
  }
}

function* authRestorePasswordWatch() {
  yield takeLatest(AUTH_RESTORE_PASSWORD._REQUEST, authRestorePassword);
}

function* authResetWithToken({
  params: { token, new_password } = {},
  navigate,
}) {
  try {
    yield put({ type: LOADER_OPEN });
    yield call(auth.resetWithToken, { token, new_password });
    yield put({
      type: AUTH_LOGOUT._REQUEST,
      navigate,
      url: routes.auth.path,
      skipTracking: true,
    });
    toast.success(<ToastSuccess title="Password aggiornata" />);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('sagas > auth > authResetWithToken', err);
    yield put({ type: AUTH_RESET_WITH_TOKEN._ERROR, response: err });
    toast.error(<ToastError body={err} />);
  } finally {
    yield put({ type: LOADER_CLOSE });
  }
}

function* authResetWithTokenWatch() {
  yield takeLatest(AUTH_RESET_WITH_TOKEN._REQUEST, authResetWithToken);
}

function* authLogout({ navigate, url, skipTracking = false }) {
  try {
    yield put({ type: LOADER_OPEN });
    if (!skipTracking) {
      yield put({
        type: ACTION_TRACKING._REQUEST,
        action_type: 'login_logout',
        action: 'logout',
      });
    } else yield put({ type: AUTH_LOGOUT._SUCCESS });
    if (typeof navigate === 'function') {
      yield navigate(url || routes.public.path);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('sagas > auth > authLogout', err);
  } finally {
    yield put({ type: LOADER_CLOSE });
  }
}

function* authLogoutWatch() {
  yield takeLatest(AUTH_LOGOUT._REQUEST, authLogout);
}

export default [
  authLoginWatch(),
  otpAuthLoginWatch(),
  authLoginSSOWatch(),
  authLogoutWatch(),
  authResetWithTokenWatch(),
  authRestorePasswordWatch(),
];
